/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SummaryState,
  SearchState,
  IntegratedFiltering,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  TableHeaderRow,
  Toolbar,
  VirtualTable,
  ExportPanel,
  SearchPanel,
} from '@devexpress/dx-react-grid-material-ui';
import { getRowId } from '../../common';
import { covertToTimeDateDigit } from '../../Shared/colorFormat';
import {
  fade,
  Typography,
  withStyles,
  Box,
  Button,
  Paper,
  IconButton,
} from '@material-ui/core';
import { useLazyQuery } from '@apollo/client';
import { GridExporter } from '@devexpress/dx-react-grid-export';
import saveAs from 'file-saver';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { SearchTable } from '../../components';
import useRentitems from '../../hooks/useRentitems';
import { getRentitemsReport } from '../../graphql';
import { getYearsQtyFrom } from '../../common/helpers';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { FullReportPDF, IndividualReportPDF } from './RentReportPDFs';

const styles = (theme) => ({
  tableStriped: {
    '& tbody tr:nth-of-type(odd)': {
      backgroundColor: fade(theme.palette.primary.main, 0.05),
    },
  },
});

const TableComponentBase = ({ classes, ...restProps }) => (
  <VirtualTable.Table {...restProps} className={classes.tableStriped} />
);
export const TableComponent = withStyles(styles, { name: 'TableComponent' })(
  TableComponentBase,
);
export const TableComponent2 = withStyles(
  {},
  { name: 'TableComponent' },
)(TableComponentBase);

export default function RentitemsReport({ isRTL, words }: any) {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [showOnlyWithRent, setShowOnlyWithRent] = useState(true);
  const fullReportRef = useRef(null);
  const [columns] = useState([
    { name: 'itemName', title: words.name },
    { name: 'rentitemcost', title: 'تكلفة التأجير' },
    { name: 'totalqty', title: 'عدد مرات التأجير' },
    { name: 'timedepreciation', title: 'اهتلاك سنوي' },
    { name: 'rentdepreciation', title: 'اهتلاك التأجير' },
    { name: 'totalincome', title: 'الدخل' },
    { name: 'totaldepreciation', title: 'مجموع الاهلاك' },
    { name: 'remaining', title: 'المتبقي' },
    { name: 'download', title: isRTL ? 'تحميل' : 'Download' },
  ]);

  const [tableColumnExtensions] = useState([
    { columnName: 'itemName', width: 250 },
    { columnName: 'rentitemcost', width: 150 },
    { columnName: 'totalqty', width: 120 },
    { columnName: 'timedepreciation', width: 150 },
    { columnName: 'rentdepreciation', width: 150 },
    { columnName: 'totalincome', width: 150 },
    { columnName: 'totaldepreciation', width: 150 },
    { columnName: 'remaining', width: 150 },
    { columnName: 'download', width: 100 },
  ]);

  const [getSummary, summaryData]: any = useLazyQuery(getRentitemsReport, {
    fetchPolicy: 'cache-and-network',
  });
  const { rentitems } = useRentitems();
  const { height } = useWindowDimensions();

  useEffect(() => {
    const slsData = summaryData?.data?.['getRentitemsReport']?.data || '[]';
    const rs = JSON.parse(slsData);
    if (rs && rs?.length > 0) {
      const rrs = rs.map((r: any) => {
        const item = rentitems?.find((it: any) => it?._id === r.itemId);
        if (item) {
          const yearsFromPurchase = getYearsQtyFrom(item?.purchaseDate);
          const timedepreciation =
            (item?.yeardepreciation || 0) * yearsFromPurchase;
          const rentdepreciation =
            (item?.rentdepreciation || 0) * (r?.totalqty || 0);
          const totalincome = (item?.price || 0) * (r?.totalqty || 0);
          const totaldepreciation = rentdepreciation + timedepreciation;
          const remaining = (item?.rentitemcost || 0) - totaldepreciation;
          return {
            ...r,
            _id: item?._id,
            rentitemcost: item.rentitemcost,
            totalqty: r.totalqty,
            timedepreciation,
            rentdepreciation,
            totalincome,
            totaldepreciation,
            remaining,
          };
        } else {
          return r;
        }
      });
      // Add download column to each row
      const rowsWithDownload = rrs.map((item: any) => ({
        ...item,
        download: (
          <IconButton
            size="small"
            onClick={() => generateIndividualPDF(item)}
            title={isRTL ? 'تحميل تقرير فردي' : 'Download Individual Report'}
          >
            <PictureAsPdfIcon style={{ fontSize: 16, color: '#1976d2' }} />
          </IconButton>
        ),
      }));
      setRows(rowsWithDownload);
      // Filter items with total rent > 0
      const filtered = rowsWithDownload.filter(
        (item: any) => (item.totalincome || 0) > 0,
      );
      setFilteredRows(filtered);
    }
  }, [summaryData, rentitems]);

  const fetchData = () => {
    const itemIds = rentitems?.map((ri: any) => ri._id);
    if (itemIds && itemIds?.length > 0) {
      const variables = {
        itemIds,
      };
      getSummary({
        variables,
      });
    }
  };

  useEffect(() => {
    if (rentitems && rentitems?.length > 0) {
      fetchData();
    }
  }, [rentitems]);

  const exporterRef: any = useRef(null);

  const startExport = useCallback(() => {
    exporterRef.current.exportGrid();
  }, [exporterRef]);

  const onSave = (workbook: any) => {
    workbook.xlsx.writeBuffer().then((buffer: any) => {
      const now = new Date();
      const name = `events-report-${covertToTimeDateDigit(now)}`;
      saveAs(
        new Blob([buffer], { type: 'application/octet-stream' }),
        `${name}.xlsx`,
      );
    });
  };

  const displayRows = showOnlyWithRent ? filteredRows : rows;

  const generateFullReport = () => {
    if (fullReportRef.current) {
      // Create a custom landscape PDF generation
      const element = fullReportRef.current;
      import('jspdf').then((jsPDF) => {
        import('html2canvas').then((html2canvas) => {
          html2canvas
            .default(element, {
              logging: false,
              useCORS: true,
              scale: 2, // Higher scale for better text rendering
            })
            .then((canvas) => {
              const imgWidth = 297; // A4 landscape width in mm
              const imgHeight = (canvas.height * imgWidth) / canvas.width;

              const doc = new jsPDF.default('l', 'mm', 'a4'); // 'l' for landscape
              doc.addImage(
                canvas,
                'PNG',
                0,
                0,
                imgWidth,
                imgHeight,
                '',
                'FAST',
              );
              doc.save(
                `rent-items-full-report-${new Date().toISOString().split('T')[0]}.pdf`,
              );
            });
        });
      });
    }
  };

  const generateIndividualPDF = (item: any) => {
    const element = document.createElement('div');
    element.innerHTML =
      document.getElementById(`individual-report-${item._id}`)?.innerHTML || '';
    document.body.appendChild(element);

    import('jspdf').then((jsPDF) => {
      import('html2canvas').then((html2canvas) => {
        html2canvas
          .default(element, {
            logging: false,
            useCORS: true,
            scale: 2, // Higher scale for better text rendering
          })
          .then((canvas) => {
            const imgWidth = 210;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            const doc = new jsPDF.default('p', 'mm', 'a4');
            doc.addImage(canvas, 'PNG', 0, 0, imgWidth, imgHeight, '', 'FAST');
            doc.save(`rent-item-${item.itemName || item._id}.pdf`);

            document.body.removeChild(element);
          });
      });
    });
  };
  return (
    <>
      {/* Report Controls */}
      <Box mb={2} p={2} component={Paper}>
        <Typography variant="h6" gutterBottom>
          {isRTL ? 'تقارير البنود المؤجرة' : 'Rent Items Reports'}
        </Typography>
        <Box display="flex" alignItems="center" flexWrap="wrap">
          <Button
            variant="contained"
            color="primary"
            startIcon={<PictureAsPdfIcon />}
            onClick={generateFullReport}
            disabled={displayRows.length === 0}
          >
            {isRTL ? 'تقرير شامل (PDF)' : 'Full Report (PDF)'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShowOnlyWithRent(!showOnlyWithRent)}
          >
            {showOnlyWithRent
              ? isRTL
                ? 'عرض كل البنود'
                : 'Show All Items'
              : isRTL
                ? 'عرض البنود ذات الإيجار فقط'
                : 'Show Items With Rent Only'}
          </Button>
        </Box>
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 8 }}
        >
          {isRTL
            ? `عدد البنود المعروضة: ${displayRows.length} من ${rows.length}`
            : `Showing ${displayRows.length} of ${rows.length} items`}
        </Typography>
      </Box>

      {/* Hidden Full Report for PDF Generation */}
      <div
        ref={fullReportRef}
        style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
      >
        <FullReportPDF data={displayRows} isRTL={isRTL} words={words} />
      </div>

      {/* Hidden Individual Reports for PDF Generation */}
      {displayRows.map((item: any) => (
        <div
          key={item._id}
          id={`individual-report-${item._id}`}
          style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
        >
          <IndividualReportPDF item={item} isRTL={isRTL} words={words} />
        </div>
      ))}

      {/* Data Grid */}
      <Grid rows={displayRows} columns={columns} getRowId={getRowId}>
        <SummaryState
          totalItems={[
            { columnName: 'totalqty', type: 'sum' },
            { columnName: 'totalincome', type: 'sum' },
            { columnName: 'totaldepreciation', type: 'sum' },
          ]}
        />
        <SearchState />
        <IntegratedFiltering />
        <VirtualTable
          height={height - 100}
          tableComponent={TableComponent2}
          columnExtensions={tableColumnExtensions}
          messages={{
            noData: isRTL ? 'لا يوجد بيانات' : 'no data',
          }}
          estimatedRowHeight={30}
        />
        <TableHeaderRow
          titleComponent={({ children }) => {
            return (
              <Typography style={{ fontSize: 14, fontWeight: 'bold' }}>
                {children}
              </Typography>
            );
          }}
        />
        <Toolbar />
        <SearchPanel
          inputComponent={(props: any) => {
            return <SearchTable isRTL={isRTL} {...props}></SearchTable>;
          }}
        />
        <ExportPanel startExport={startExport} />
      </Grid>
      <GridExporter
        ref={exporterRef}
        rows={rows}
        columns={columns}
        onSave={onSave}
      />
    </>
  );
}
